import { bankersRound, exportCSVFile, getUserUnitLabel, useAppUser } from '@bechtel/client-common';
import { Box, Button, ButtonAppearance, ButtonSize, Icon, IconSize, Spinner } from '@bechtel/gds';
import { MilestoneStatusCode, WeeklyWorkListBulkItem } from '@bechtel/types';
import { t } from '@lingui/macro';
import { forEach, isNull, startCase, toLower } from 'lodash';
import { FC, Fragment, useEffect } from 'react';
import { DownloadWorkListProps, DownloadWWLCSVData } from './types';

export const DownloadWorkList: FC<DownloadWorkListProps> = ({
  isWWLDownload = false,
  workListItems,
  loading,
  downloadWWL,
  downloadDataReady,
  setDownloadDataReady,
  milestoneStatus,
  userName,
  selectedWeek,
  disabled,
}) => {
  const { user } = useAppUser();
  useEffect(() => {
    if (!downloadDataReady || workListItems.length === 0) return;
    let downloadWWLCSVDataList: DownloadWWLCSVData[] = [];
    const downloadHeaders = {
      milestoneName: t`Milestone`,
      quantity: t`Quantity`,
      commodityType: t`Commodity Type`,
      commodityTag: t`Tag Number`,
      costCodeId: t`Cost Code`,
      workpackageTag: t`Work Package`,
      foreman: t`Foreman`,
      generalForeman: t`General Foreman`,
    };
    let uom = getUserUnitLabel(user, true);

    downloadHeaders.quantity = t`Quantity ` + `(${uom})`;
    forEach(workListItems, (item: WeeklyWorkListBulkItem) => {
      const downloadWorkListItem: DownloadWWLCSVData = {
        milestoneName: item.milestoneType,
        quantity: bankersRound(item.quantityHours),
        commodityType: item.commodityType,
        commodityTag: item.tag,
        costCodeId: item?.costCodeId ? item.costCodeId : '',
        workpackageTag: item.workPackageNumber,
        foreman: item?.foreman ? item.foreman : '',
        generalForeman: item?.generalForeman ? item.generalForeman : '',
        plannedWeekNumber: String(item.planWeekNumber ? item.planWeekNumber : ''),
        claimedWeekNumber: selectedWeek,
      };
      downloadWWLCSVDataList.push(downloadWorkListItem);
    });
    const fileName = isWWLDownload
      ? t`WorkList` + startCase(toLower(milestoneStatus || MilestoneStatusCode.PLANNED)) + t`-Week` + selectedWeek
      : `${userName}-${
          milestoneStatus === MilestoneStatusCode.APPROVED
            ? t`Approved List-Week` + selectedWeek
            : t`Pending Approval List`
        }`;
    if (!isWWLDownload) {
      downloadWWLCSVDataList = downloadWWLCSVDataList.map(
        ({
          milestoneName,
          quantity,
          commodityType,
          commodityTag,
          costCodeId,
          workpackageTag,
          foreman,
          generalForeman,
        }) => ({
          milestoneName,
          quantity,
          commodityType,
          commodityTag,
          costCodeId,
          workpackageTag,
          foreman,
          generalForeman,
        })
      );
    } else {
      downloadHeaders['plannedWeekNumber'] = t`Planned Week Number`;
      if (milestoneStatus === MilestoneStatusCode.PLANNED || isNull(milestoneStatus) ) {
        downloadWWLCSVDataList = downloadWWLCSVDataList.map(
          ({
            milestoneName,
            quantity,
            commodityType,
            commodityTag,
            costCodeId,
            workpackageTag,
            foreman,
            generalForeman,
            plannedWeekNumber,
          }) => ({
            milestoneName,
            quantity,
            commodityType,
            commodityTag,
            costCodeId,
            workpackageTag,
            foreman,
            generalForeman,
            plannedWeekNumber,
          })
        );
      } else {
        downloadHeaders['claimedWeekNumber'] = t`Claimed Week Number`;
      }
    }

    exportCSVFile(downloadHeaders, downloadWWLCSVDataList, fileName);
    setDownloadDataReady(false);
  }, [downloadDataReady, workListItems.length]);

  return (
    <Fragment>
      <Box mr={{ sm: '400' }}>
        <Button
          disabled={disabled}
          size={ButtonSize.SMALL}
          appearance={ButtonAppearance.SECONDARY}
          onClick={(e) => {
            downloadWWL();
            e.preventDefault();
          }}
        >
          {loading && <Spinner />}
          {!loading && <Icon icon="download" size={IconSize.MEDIUM} fillColor="blue.800" />}
        </Button>
      </Box>
    </Fragment>
  );
};